import React, { useState } from "react";

import { monday, type MondayStreamIntegrationDetails } from ".";

import { useSyncedPersistentStateWithOverride } from "~/actionItems/hooks/useSyncedPersistentState";
import { ReactComponent as MondayLogo } from "~/legacy-assets/images/logos/monday.svg";
import {
	type ActionItemIntegrationCardProps,
	connectStreamIntegration,
	type IntegrationConfigProps,
	StreamSettingsIntegrationCard,
} from "~/stream/StreamSettings/ActionItemIntegrations";
import { Checkbox } from "~/ui";
import { Button } from "~/ui";

import { getIntegrationForKey } from "../actionItemIntegration";
import { BoardSelector } from "./selectors/MondayBoardSelector";
import { ColumnsSelectors } from "./selectors/MondayColumnsSelectors";
import { GroupSelector } from "./selectors/MondayGroupSelector";
import { mondayStore } from "./store";

import mondayModal from "./css/mondayModal.module.css";

export function MondayStreamSettingsIntegrationCard({ viewer, onClickIntegration }: ActionItemIntegrationCardProps) {
	const onClick = React.useCallback(() => {
		onClickIntegration(getIntegrationForKey("MONDAY"));
	}, [onClickIntegration]);

	return <StreamSettingsIntegrationCard integration={monday} onClickIntegration={onClick} viewer={viewer} />;
}

export function MondayStreamSettingsConfigView({
	integration,
	stream,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps<MondayStreamIntegrationDetails>) {
	const [boardId, setBoardId] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"boardId",
		integrationDetails?.externalBoardId,
	);
	const [groupId, setGroupId] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"groupId",
		integrationDetails?.externalGroupId,
	);
	const [groupName, setGroupName] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"groupName",
		integrationDetails?.externalGroupName,
	);
	const [boardName, setBoardName] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"boardName",
		integrationDetails?.externalBoardName,
	);
	const [assigneeColumnId, setAssigneeColumnId] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"assigneeColumnId",
		integrationDetails?.assigneeColumnId,
	);
	const [statusColumnId, setStatusColumnId] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"statusColumnId",
		integrationDetails?.statusColumnId,
	);
	const [dueDateColumnId, setDueDateColumnId] = useSyncedPersistentStateWithOverride<string | undefined>(
		mondayStore,
		"dueDateColumnId",
		integrationDetails?.dueDateColumnId,
	);
	const [includeAiGenerated, setIncludeAiGenerated] = useState<boolean>(integrationDetails?.includeAiGenerated ?? true);

	const { displayName } = integration;

	const handleIncludeAiGeneratedChange = React.useCallback((checked: boolean) => {
		setIncludeAiGenerated(checked);
	}, []);

	const submit = React.useCallback(async () => {
		if (!boardId || !groupId || !stream || !stream.id || !groupName || !boardName) {
			throw new Error("One or more fields are invalid");
		}

		const destinationInfo = {
			/* eslint-disable camelcase */
			board_id: boardId,
			group_id: groupId,
			group_name: groupName,
			board_name: boardName,
			assignee_column_id: assigneeColumnId,
			status_column_id: statusColumnId,
			due_date_column_id: dueDateColumnId,
			include_ai_generated: includeAiGenerated,
			/* eslint-enable camelcase */
		};

		return connectStreamIntegration("MONDAY", destinationInfo, stream.id).then(() => {
			onFinish(integration);
		});
	}, [
		stream,
		integration,
		boardId,
		groupId,
		groupName,
		boardName,
		assigneeColumnId,
		statusColumnId,
		dueDateColumnId,
		includeAiGenerated,
		onFinish,
	]);

	return (
		<>
			<MondayLogo />
			<div className={mondayModal.settingsTitle}>Select a {displayName} Group</div>
			<p className={mondayModal.description}>
				Sync action items between the note series <b>{stream?.displayName}</b> and {displayName}.
			</p>
			<div className={mondayModal.configSettingsContainer}>
				<label className={mondayModal.board}>
					<BoardSelector boardId={boardId} setBoardId={setBoardId} setBoardName={setBoardName} />
				</label>
				<label className={mondayModal.group}>
					<GroupSelector boardId={boardId} groupId={groupId} setGroupId={setGroupId} setGroupName={setGroupName} />
				</label>
				<ColumnsSelectors
					boardId={boardId}
					assigneeColumnId={assigneeColumnId}
					dueDateColumnId={dueDateColumnId}
					statusColumnId={statusColumnId}
					setAssigneeColumnId={setAssigneeColumnId}
					setDueDateColumnId={setDueDateColumnId}
					setStatusColumnId={setStatusColumnId}
					showLabels={false}
				/>
			</div>
			<div className={mondayModal.toggleOption}>
				<Checkbox
					id="include-ai-generated"
					checked={includeAiGenerated}
					onCheckedChange={handleIncludeAiGeneratedChange}
				>
					Include AI-generated action items
				</Checkbox>
				<span className={mondayModal.toggleDescription}>
					When enabled, AI-generated action items will be sent to this integration.
				</span>
			</div>
			<p>
				Action items <b>created after connecting</b> will be sent to {displayName}.
			</p>
			<div className={mondayModal.buttonRow}>
				<Button size="normal" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={submit} disabled={!groupId}>
					Connect
				</Button>
			</div>
		</>
	);
}
